import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatEventDate, formatPrice, formatAmount } from "@/lib/format";
import SeatMapView from "@/components/SeatMapView";
import { Calendar, MapPin, Users, Vote, BarChart3, Tag, Clock, Minus, Plus, Ticket as TicketIcon, BellRing, MapPinned, Gift } from "lucide-react";
import { toast } from "sonner";
import ZButton from "@/components/ZButton";
import { ZInput } from "@/components/ZInput";
import Modal from "@/components/Modal";
import TermsConsent from "@/components/TermsConsent";
import VerifiedBadge from "@/components/VerifiedBadge";
import ShareCelebrationModal from "@/components/ShareCelebrationModal";

function isOnSale(cat) {
  const now = new Date();
  if (cat.sale_starts_at && new Date(cat.sale_starts_at) > now) return false;
  if (cat.sale_ends_at && new Date(cat.sale_ends_at) < now) return false;
  return true;
}

function CategoryCard({ cat, currency, onSelect, selected, onWaitlist }) {
  const onSale = isOnSale(cat);
  const soldOut = cat.available <= 0;
  const disabled = !onSale || soldOut;
  const endsAt = cat.sale_ends_at ? new Date(cat.sale_ends_at) : null;
  const handleClick = () => {
    if (soldOut && onWaitlist) { onWaitlist(cat); return; }
    if (disabled) return;
    onSelect(cat);
  };
  return (
    <div
      className="z-card"
      style={{
        padding: 16,
        border: selected ? "2px solid var(--z-teal)" : "1px solid var(--z-border, #eee)",
        opacity: !onSale && !soldOut ? 0.55 : 1,
        cursor: !onSale && !soldOut ? "not-allowed" : "pointer",
        transition: "border-color .15s",
      }}
      data-testid={`category-card-${cat.id}`}
      onClick={handleClick}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div style={{ flex: 1 }}>
          <h4 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 4px", color: "var(--z-text)" }}>{cat.name}</h4>
          {cat.description && <p style={{ fontSize: 13, color: "var(--z-muted)", margin: "0 0 8px" }}>{cat.description}</p>}
        </div>
        <span className="z-event-card-price" style={{ fontSize: 16, padding: "6px 12px", whiteSpace: "nowrap" }} data-testid={`category-price-${cat.id}`}>
          {formatPrice(cat.price, currency)}
        </span>
      </div>
      <div style={{ display: "flex", gap: 10, fontSize: 12, color: "var(--z-muted)", marginTop: 8, flexWrap: "wrap" }}>
        <span><Users size={12} style={{ verticalAlign: "middle" }} /> {cat.available} dispo / {cat.capacity}</span>
        {endsAt && onSale && (
          <span style={{ color: "var(--z-orange)" }}><Clock size={12} style={{ verticalAlign: "middle" }} /> Jusqu&apos;au {endsAt.toLocaleDateString("fr-FR")}</span>
        )}
        {soldOut && <span style={{ color: "var(--z-error,#c00)", fontWeight: 600 }}>Complet — me prévenir</span>}
        {!soldOut && !onSale && <span style={{ color: "var(--z-error,#c00)" }}>Indisponible</span>}
      </div>
    </div>
  );
}

export default function EventDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [votes, setVotes] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [showPurchase, setShowPurchase] = useState(false);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseErr, setPurchaseErr] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [guestName, setGuestName] = useState("");
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoPreview, setPromoPreview] = useState(null);
  const [promoErr, setPromoErr] = useState("");
  const [promoChecking, setPromoChecking] = useState(false);
  // Waitlist
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitCat, setWaitCat] = useState(null);
  const [waitName, setWaitName] = useState("");
  const [waitEmail, setWaitEmail] = useState("");
  const [waitPhone, setWaitPhone] = useState("");
  const [acceptWaitTerms, setAcceptWaitTerms] = useState(false);
  const [waitErr, setWaitErr] = useState("");
  const [waitOk, setWaitOk] = useState(false);
  const [waitSubmitting, setWaitSubmitting] = useState(false);
  // Seat map
  const [seatMap, setSeatMap] = useState(null);
  const [selectedSeats, setSelectedSeats] = useState([]);  // array of seat ids
  // Share celebration
  const [celebration, setCelebration] = useState(null); // { ticketId, qrToken }
  // Gift list (if event has one published)
  const [giftList, setGiftList] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get(`/events/${id}`).then((r) => setEvent(r.data)),
      api.get(`/events/${id}/votes`).then((r) => setVotes(r.data)).catch(() => {}),
      api.get(`/events/${id}/surveys`).then((r) => setSurveys(r.data)).catch(() => {}),
      api.get(`/events/${id}/seat-map`).then((r) => setSeatMap(r.data)).catch(() => setSeatMap(null)),
      api.get(`/public/gift-lists/event/${id}`).then((r) => setGiftList(r.data)).catch(() => setGiftList(null)),
    ]).finally(() => setLoading(false));
  }, [id]);

  const categories = event?.categories || [];
  const hasRealCategories = categories.length > 0 && categories[0].id !== "legacy";
  const unitPrice = selectedCat ? selectedCat.price : (event?.price || 0);
  const subtotal = unitPrice * quantity;
  const discount = promoPreview ? promoPreview.discount : 0;
  const total = Math.max(0, subtotal - discount);

  const startPurchase = (cat) => {
    setPurchaseErr("");
    setSelectedCat(cat);
    setQuantity(1);
    setPromoCode("");
    setPromoPreview(null);
    setPromoErr("");
    setSelectedSeats([]);
    setAcceptTerms(false);
    setShowPurchase(true);
  };

  const toggleSeat = (seatId, zoneId) => {
    const zone = seatMap?.zones.find((z) => z.id === zoneId);
    if (!zone) return;
    const cat = (event?.categories || []).find((c) => c.id === zone.category_id);
    // If no selectedCat yet, lock to this zone's category
    if (!selectedCat) {
      setSelectedCat(cat);
      setSelectedSeats([seatId]);
      return;
    }
    if (selectedCat.id !== cat?.id) return;  // ignore (UI dims wrong category)
    if (selectedSeats.includes(seatId)) {
      const next = selectedSeats.filter((s) => s !== seatId);
      setSelectedSeats(next);
      if (next.length === 0) setSelectedCat(null);
    } else {
      setSelectedSeats([...selectedSeats, seatId]);
    }
  };

  const openSeatPurchase = () => {
    if (selectedSeats.length === 0 || !selectedCat) return;
    setPurchaseErr("");
    setQuantity(selectedSeats.length);
    setPromoCode("");
    setPromoPreview(null);
    setPromoErr("");
    setAcceptTerms(false);
    setShowPurchase(true);
  };

  const openWaitlist = (cat) => {
    setWaitErr("");
    setWaitOk(false);
    setWaitCat(cat);
    setWaitName(user?.name || "");
    setWaitEmail(user?.email || "");
    setWaitPhone(user?.phone || "");
    setAcceptWaitTerms(false);
    setShowWaitlist(true);
  };

  const submitWaitlist = async () => {
    setWaitErr("");
    if (!waitName.trim() || !waitEmail.trim()) { setWaitErr("Nom et e-mail requis."); return; }
    if (!acceptWaitTerms) { setWaitErr("Vous devez accepter les CGV et la politique de confidentialité."); return; }
    setWaitSubmitting(true);
    try {
      const payload = { name: waitName.trim(), email: waitEmail.trim() };
      if (waitPhone.trim()) payload.phone = waitPhone.trim();
      if (waitCat && waitCat.id !== "legacy") payload.category_id = waitCat.id;
      const r = await api.post(`/events/${id}/waitlist`, payload);
      setWaitOk(true);
      if (r.data.already) {
        setWaitErr("Vous êtes déjà sur la liste d'attente.");
      }
    } catch (e) {
      setWaitErr(errMsg(e));
    } finally {
      setWaitSubmitting(false);
    }
  };

  const checkPromo = async () => {
    setPromoErr("");
    setPromoPreview(null);
    if (!promoCode.trim()) return;
    setPromoChecking(true);
    try {
      const r = await api.post(`/events/${id}/validate-promo`, {
        code: promoCode.trim().toUpperCase(),
        category_id: selectedCat?.id || null,
        quantity,
      });
      setPromoPreview(r.data);
    } catch (e) {
      setPromoErr(errMsg(e));
    } finally {
      setPromoChecking(false);
    }
  };

  const submitPurchase = async () => {
    setPurchaseErr("");
    if (!acceptTerms) {
      setPurchaseErr("Vous devez accepter les CGV et la politique de confidentialité.");
      return;
    }
    setPurchasing(true);
    try {
      const payload = { quantity };
      if (selectedCat && selectedCat.id !== "legacy") payload.category_id = selectedCat.id;
      if (promoCode.trim()) payload.promo_code = promoCode.trim().toUpperCase();
      if (seatMap && seatMap.mode === "seats" && selectedSeats.length > 0) {
        payload.seat_ids = selectedSeats;
        payload.quantity = selectedSeats.length;
      }
      if (!user) {
        if (!guestName.trim() || !guestEmail.trim()) {
          setPurchaseErr("Renseignez votre nom et votre e-mail.");
          setPurchasing(false);
          return;
        }
        payload.guest_name = guestName.trim();
        payload.guest_email = guestEmail.trim();
        if (guestPhone.trim()) payload.guest_phone = guestPhone.trim();
      }
      const r = await api.post(`/events/${id}/purchase`, payload);
      const ticket = r.data.tickets[0];
      setShowPurchase(false);
      setSelectedSeats([]);
      const tokenParam = !user ? `?token=${encodeURIComponent(ticket.qr_payload)}` : "";
      // Show celebration / share modal first; user can then go to their ticket.
      setCelebration({
        ticketId: ticket.id,
        tokenParam,
      });
    } catch (e) {
      setPurchaseErr(errMsg(e));
    } finally {
      setPurchasing(false);
    }
  };

  if (loading) return <div className="z-container" style={{ textAlign: "center" }}><span className="z-loader z-loader-dark" /></div>;
  if (!event) return <div className="z-container">Événement introuvable.</div>;

  return (
    <div data-testid="event-detail-page">
      {event.image_url && (
        <div style={{ width: "100%", aspectRatio: "16/9", maxHeight: 360, overflow: "hidden" }}>
          <img src={event.image_url} alt={event.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
        </div>
      )}

      <div className="z-container">
        <h1 className="z-page-title" data-testid="event-title">{event.title}</h1>
        <p style={{ color: "var(--z-muted)", margin: "4px 0 18px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span>{event.organizer_name}</span>
          <VerifiedBadge verified={event.organizer_verified} variant="pill" testId="event-detail-verified" />
        </p>

        <div className="z-card" style={{ padding: 18, marginBottom: 20 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <Calendar size={18} color="var(--z-teal)" />
            <span data-testid="event-date">{formatEventDate(event.starts_at)}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <MapPin size={18} color="var(--z-teal)" />
            <span data-testid="event-location">{event.location || "Lieu communiqué"}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Users size={18} color="var(--z-teal)" />
            <span data-testid="event-capacity">
              {event.tickets_sold} / {event.capacity} places réservées
            </span>
          </div>
        </div>

        <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 8px" }}>À propos</h3>
        <p style={{ color: "var(--z-text)", lineHeight: 1.6, whiteSpace: "pre-wrap", marginBottom: 22 }} data-testid="event-description">
          {event.description}
        </p>

        <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 12px", display: "flex", alignItems: "center", gap: 8 }}>
          {seatMap ? <MapPinned size={18} color="var(--z-teal)" /> : <TicketIcon size={18} color="var(--z-teal)" />}
          {seatMap ? "Plan de salle" : (hasRealCategories ? "Catégories disponibles" : "Billet")}
        </h3>

        {seatMap ? (
          <div style={{ marginBottom: 28 }} data-testid="seatmap-block">
            {/* Legend (categories) */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
              {categories.filter((c) => c.id !== "legacy").map((c) => {
                const zone = seatMap.zones.find((z) => z.category_id === c.id);
                const isActive = selectedCat?.id === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      if (selectedCat?.id === c.id) { setSelectedCat(null); setSelectedSeats([]); }
                      else {
                        if (selectedSeats.length > 0) {
                          toast.info(`Sélection de ${selectedSeats.length} siège(s) effacée — changement de catégorie`);
                        }
                        setSelectedCat(c);
                        setSelectedSeats([]);
                      }
                    }}
                    className="z-card"
                    style={{
                      padding: "8px 12px",
                      border: isActive ? `2px solid ${zone?.color || "#01696F"}` : "1px solid var(--z-border)",
                      background: isActive ? `${zone?.color || "#01696F"}15` : "white",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      cursor: "pointer",
                    }}
                    data-testid={`seatmap-cat-${c.id}`}
                  >
                    <span style={{ width: 14, height: 14, background: zone?.color || "#01696F", borderRadius: 4 }} />
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{c.name}</span>
                    <span style={{ color: "var(--z-teal)", fontWeight: 700, fontSize: 13 }}>{formatPrice(c.price, event.currency)}</span>
                    <span style={{ color: "var(--z-muted)", fontSize: 12 }}>· {c.available} dispo</span>
                  </button>
                );
              })}
            </div>

            <SeatMapView
              map={seatMap}
              mode="viewer"
              selectedSeats={selectedSeats}
              onSeatToggle={toggleSeat}
              onZoneClick={(zid) => {
                // zones-mode: clicking a zone opens purchase modal for that category
                if (seatMap.mode === "zones") {
                  const z = seatMap.zones.find((zz) => zz.id === zid);
                  const c = categories.find((cc) => cc.id === z?.category_id);
                  if (c) startPurchase(c);
                }
              }}
              highlightCategoryId={selectedCat?.id}
              maxSelectable={10}
            />

            {seatMap.mode === "seats" && (
              <div style={{ marginTop: 12, padding: 12, background: "var(--z-bg)", borderRadius: 10, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
                <div style={{ fontSize: 14 }}>
                  {selectedSeats.length === 0 ? (
                    <span style={{ color: "var(--z-muted)" }}>Cliquez sur les sièges pour les sélectionner.</span>
                  ) : (
                    <>
                      <strong>{selectedSeats.length} siège(s)</strong> sélectionné(s) : {selectedSeats.map((sid) => {
                        for (const z of seatMap.zones) {
                          const s = (z.seats || []).find((ss) => ss.id === sid);
                          if (s) return s.label;
                        }
                        return null;
                      }).filter(Boolean).join(", ")}
                    </>
                  )}
                </div>
                <ZButton onClick={openSeatPurchase} disabled={selectedSeats.length === 0} data-testid="seatmap-checkout-btn">
                  Continuer ({selectedSeats.length})
                </ZButton>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 12, marginBottom: 28 }} data-testid="categories-grid">
            {categories.map((cat) => (
              <CategoryCard key={cat.id} cat={cat} currency={event.currency} onSelect={startPurchase} selected={selectedCat?.id === cat.id} onWaitlist={openWaitlist} />
            ))}
          </div>
        )}

        {giftList && (
          <Link to={`/liste/${giftList.slug}`}
            className="z-card"
            data-testid="event-gift-list-banner"
            style={{
              marginTop: 18, padding: 16,
              display: "flex", alignItems: "center", gap: 12,
              textDecoration: "none", color: "inherit",
              background: "linear-gradient(135deg, #FCE2C3 0%, #FFF7EB 100%)",
              border: "1px solid #DA710133",
            }}>
            <div style={{ background: "#DA7101", color: "white", width: 44, height: 44, borderRadius: 12, display: "grid", placeItems: "center" }}>
              <Gift size={22} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, color: "#0F1418" }}>{giftList.title}</div>
              <div style={{ fontSize: 13, color: "#5B6671" }}>Découvrez la liste de cadeaux — réservez ou contribuez en un clic</div>
            </div>
            <span style={{ color: "#DA7101", fontWeight: 700, fontSize: 13 }}>Voir →</span>
          </Link>
        )}

        {(votes.length > 0 || surveys.length > 0) && (
          <div style={{ marginTop: 18 }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, margin: "0 0 12px" }}>Participer</h3>
            <div style={{ display: "grid", gap: 10 }}>
              {votes.map((v) => (
                <Link key={v.id} to={`/vote/${v.id}`} className="z-card" style={{ padding: 14, display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: "inherit" }} data-testid={`event-vote-${v.id}`}>
                  <Vote size={20} color="var(--z-teal)" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{v.title}</div>
                    <div style={{ fontSize: 13, color: "var(--z-muted)" }}>Vote — {v.total_votes} participation(s)</div>
                  </div>
                  {v.user_voted && <span className="z-badge z-badge-success">Voté</span>}
                </Link>
              ))}
              {surveys.map((s) => (
                <Link key={s.id} to={`/survey/${s.id}`} className="z-card" style={{ padding: 14, display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: "inherit" }} data-testid={`event-survey-${s.id}`}>
                  <BarChart3 size={20} color="var(--z-teal)" />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600 }}>{s.title}</div>
                    <div style={{ fontSize: 13, color: "var(--z-muted)" }}>Sondage — {s.questions.length} question(s)</div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      <Modal open={showPurchase} onClose={() => setShowPurchase(false)} title={user ? "Confirmer mon billet" : "Acheter sans compte"} testId="purchase-modal">
        {selectedCat && (
          <div style={{ background: "var(--z-bg)", padding: 12, borderRadius: 10, marginBottom: 14 }}>
            <div style={{ fontSize: 13, color: "var(--z-muted)" }}>Catégorie</div>
            <div style={{ fontWeight: 700, fontSize: 15 }} data-testid="purchase-category-name">{selectedCat.name}</div>
            <div style={{ fontSize: 13, color: "var(--z-teal)", fontWeight: 600 }}>{formatPrice(selectedCat.price, event.currency)} / billet</div>
            {seatMap && seatMap.mode === "seats" && selectedSeats.length > 0 && (
              <div style={{ marginTop: 6, fontSize: 13 }} data-testid="purchase-seats-list">
                <strong>Sièges :</strong> {selectedSeats.map((sid) => {
                  for (const z of seatMap.zones) {
                    const s = (z.seats || []).find((ss) => ss.id === sid);
                    if (s) return s.label;
                  }
                  return null;
                }).filter(Boolean).join(", ")}
              </div>
            )}
          </div>
        )}

        {!(seatMap && seatMap.mode === "seats") && (
          <>
            <label className="z-label">Quantité</label>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <button type="button" className="z-btn z-btn-outline z-btn-sm" onClick={() => setQuantity((q) => Math.max(1, q - 1))} data-testid="qty-dec-btn"><Minus size={14} /></button>
              <input className="z-input" type="number" min="1" max={selectedCat?.available || 10} value={quantity} onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))} style={{ width: 70, textAlign: "center", margin: 0 }} data-testid="qty-input" />
              <button type="button" className="z-btn z-btn-outline z-btn-sm" onClick={() => setQuantity((q) => Math.min(selectedCat?.available || 10, q + 1))} data-testid="qty-inc-btn"><Plus size={14} /></button>
            </div>
          </>
        )}

        {!user && (
          <>
            <ZInput label="Nom complet" value={guestName} onChange={(e) => setGuestName(e.target.value)} placeholder="Aïssa Bah" data-testid="guest-name-input" />
            <ZInput type="email" label="E-mail" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)} placeholder="vous@exemple.com" data-testid="guest-email-input" />
            <ZInput type="tel" label="Téléphone (optionnel, pour recevoir un SMS)" value={guestPhone} onChange={(e) => setGuestPhone(e.target.value)} placeholder="+224 620 00 00 00" data-testid="guest-phone-input" />
          </>
        )}

        <label className="z-label" style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Tag size={14} /> Code promo (optionnel)
        </label>
        <div style={{ display: "flex", gap: 8, marginBottom: promoErr || promoPreview ? 6 : 14 }}>
          <input
            className="z-input"
            value={promoCode}
            onChange={(e) => { setPromoCode(e.target.value.toUpperCase()); setPromoPreview(null); setPromoErr(""); }}
            placeholder="FAMILY20"
            style={{ margin: 0, textTransform: "uppercase" }}
            data-testid="promo-code-input"
          />
          <button
            type="button"
            className="z-btn z-btn-outline z-btn-sm"
            onClick={checkPromo}
            disabled={!promoCode.trim() || promoChecking}
            data-testid="promo-apply-btn"
          >
            {promoChecking ? "…" : "Appliquer"}
          </button>
        </div>
        {promoErr && <p className="z-error-text" style={{ fontSize: 12, marginBottom: 10 }} data-testid="promo-error">{promoErr}</p>}
        {promoPreview && (
          <p style={{ fontSize: 12, color: "var(--z-teal)", marginBottom: 10, fontWeight: 600 }} data-testid="promo-success">
            ✓ Code appliqué : -{formatAmount(promoPreview.discount, event.currency)} ({promoPreview.discount_type === "percent" ? `${promoPreview.discount_value}%` : "fixe"})
          </p>
        )}

        <div style={{ background: "var(--z-bg)", padding: 12, borderRadius: 10, marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--z-muted)" }}>
            <span>Sous-total ({quantity} × {formatPrice(unitPrice, event.currency)})</span>
            <span>{formatAmount(subtotal, event.currency)}</span>
          </div>
          {discount > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: "var(--z-orange)", marginTop: 4 }}>
              <span>Réduction</span>
              <span>-{formatAmount(discount, event.currency)}</span>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", fontWeight: 700, marginTop: 8, paddingTop: 8, borderTop: "1px solid var(--z-border,#eee)" }}>
            <span>Total</span>
            <span style={{ color: "var(--z-teal)" }} data-testid="purchase-total">{formatPrice(total, event.currency)}</span>
          </div>
        </div>

        {purchaseErr && <p className="z-error-text" data-testid="purchase-error">{purchaseErr}</p>}
        <TermsConsent checked={acceptTerms} onChange={setAcceptTerms} testId="purchase-terms-consent" />
        <ZButton onClick={submitPurchase} loading={purchasing} disabled={!acceptTerms} block data-testid="purchase-confirm-btn">
          Confirmer (paiement simulé)
        </ZButton>
      </Modal>

      <Modal open={showWaitlist} onClose={() => setShowWaitlist(false)} title="Liste d'attente" testId="waitlist-modal">
        {waitOk && !waitErr ? (
          <div style={{ textAlign: "center", padding: "12px 0" }} data-testid="waitlist-success">
            <BellRing size={42} color="var(--z-teal)" style={{ marginBottom: 12 }} />
            <h3 style={{ margin: "0 0 8px", color: "var(--z-teal)" }}>Inscription confirmée</h3>
            <p style={{ color: "var(--z-muted)", fontSize: 14 }}>
              Nous vous écrivons dès qu&apos;une place se libère dans la catégorie <strong>{waitCat?.name}</strong>.
            </p>
            <ZButton onClick={() => setShowWaitlist(false)} block style={{ marginTop: 14 }} data-testid="waitlist-close-btn">Fermer</ZButton>
          </div>
        ) : (
          <>
            <div style={{ background: "var(--z-bg)", padding: 12, borderRadius: 10, marginBottom: 14, display: "flex", gap: 10, alignItems: "center" }}>
              <BellRing size={20} color="var(--z-orange)" />
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>Soyez prévenu en priorité</div>
                <div style={{ fontSize: 12, color: "var(--z-muted)" }}>
                  {waitCat?.name && <>Catégorie <strong>{waitCat.name}</strong> · </>}
                  Vous recevrez un e-mail si des places se libèrent.
                </div>
              </div>
            </div>
            <ZInput label="Nom complet" value={waitName} onChange={(e) => setWaitName(e.target.value)} data-testid="waitlist-name-input" />
            <ZInput type="email" label="E-mail" value={waitEmail} onChange={(e) => setWaitEmail(e.target.value)} data-testid="waitlist-email-input" />
            <ZInput type="tel" label="Téléphone (optionnel, pour un SMS)" value={waitPhone} onChange={(e) => setWaitPhone(e.target.value)} placeholder="+224 620 00 00 00" data-testid="waitlist-phone-input" />
            {waitErr && <p className="z-error-text" data-testid="waitlist-error">{waitErr}</p>}
            <TermsConsent checked={acceptWaitTerms} onChange={setAcceptWaitTerms} testId="waitlist-terms-consent" />
            <ZButton onClick={submitWaitlist} loading={waitSubmitting} disabled={!acceptWaitTerms} block data-testid="waitlist-submit-btn">
              M&apos;inscrire à la liste d&apos;attente
            </ZButton>
          </>
        )}
      </Modal>

      <ShareCelebrationModal
        open={!!celebration}
        onClose={() => {
          const target = celebration ? `/ticket/${celebration.ticketId}${celebration.tokenParam}` : "/";
          setCelebration(null);
          nav(target);
        }}
        kind="ticket"
        entityId={event.id}
        title={event.title}
        subtitle={event.organizer_name}
        dateStr={formatEventDate(event.starts_at)}
        location={event.location}
        imageUrl={event.image_url || null}
        guestEmail={!user ? (guestEmail || "").trim() : undefined}
        guestName={!user ? (guestName || "").trim() : undefined}
        primaryCtaLabel="Voir mon billet"
        onPrimaryCta={() => {
          const target = celebration ? `/ticket/${celebration.ticketId}${celebration.tokenParam}` : "/";
          setCelebration(null);
          nav(target);
        }}
      />
    </div>
  );
}
